import { Box, Stack, Typography } from '@mui/material';
import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { recordRecentVisit } from '@/components/CommandPalette/recents';
import { HelpLink } from '@/components/HelpLink';
import type { HelpStoryId } from '@/help/registry.gen';
import { publishHeaderTitle } from '@/utils/documentTitle';

interface Props {
  /** Usually a string. A node (e.g. a linked guest name) must come with
   *  `titleText` — the ⌘K "Recent" entry and the document title need plain
   *  text. */
  title: ReactNode;
  titleText?: string;
  /** Plain text or a `<DetailSubtitle>` of linked/copyable parts. */
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Beside the title, outside the heading (a record's state chips). */
  titleAdornment?: ReactNode;
  /** Before the title, outside the heading (a person's avatar). */
  leading?: ReactNode;
  /** Renders a compact "?" next to the title linking to the help story. */
  helpStory?: HelpStoryId;
  /** Label of the active tab/step, for the document title
   *  (`Acme Tours #9 | Tour operator · Configuration | Ops`). Pass it
   *  unconditionally: the route's `sectionParam` decides whether the URL
   *  actually carries a section, so a default tab is dropped for you. */
  section?: string;
}

/** The title column's share of the header row: below this much room beside the actions,
 *  the actions move to a row of their own under the title. */
const TITLE_BASIS_PX = 280;

export function PageHeader({ title, titleText, subtitle, actions, titleAdornment, leading, helpStory, section }: Props) {
  // This component is the plain-text name of whatever the page is showing, so
  // it feeds two consumers. It must stay free of data-router-only hooks
  // (`useMatches` throws under the plain `<MemoryRouter>` most tests use), so
  // it publishes strings and knows nothing about routes — `<DocumentTitle>`
  // supplies the route half.
  const location = useLocation();
  const recentTitle = typeof title === 'string' ? title : (titleText ?? '');
  useEffect(() => {
    // Detail-page visits (paths with a numeric record segment) feed the ⌘K
    // palette's "Recent" group; list pages are ignored by recordRecentVisit.
    if (recentTitle) recordRecentVisit(location.pathname, recentTitle);
    publishHeaderTitle(location.pathname, { label: recentTitle, section: section ?? null });
  }, [location.pathname, recentTitle, section]);

  // On a narrow page the actions wrap below the title rather than push the page wider: the
  // title column takes the row from TITLE_BASIS_PX on, and a title too long for one line
  // (an email as a person's name) wraps inside it — nothing overflows the page scroller.
  return (
    <Box
      data-page-header
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 1, mb: 2 }}
    >
      <Box sx={{ flex: `1 1 ${TITLE_BASIS_PX}px`, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          {/* Level with the title's first line (an h5 line is 32 px) when the title wraps. */}
          {leading && (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 32, flex: 'none', mr: 0.75 }}>{leading}</Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              minWidth: 0,
              flexWrap: titleAdornment ? 'wrap' : 'nowrap',
            }}
          >
            <Typography variant="h5" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {title}
            </Typography>
            {helpStory && <HelpLink story={helpStory} />}
            {/* Never wider than the title column: a long chip ellipsizes instead of pushing
                the page wider (each chip is clamped to the line, MUI's Chip ellipsizes). */}
            {titleAdornment && (
              <Box
                data-page-header-adornment
                sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}
              >
                {titleAdornment}
              </Box>
            )}
          </Box>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" component="div">
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          data-page-header-actions
          sx={{ flex: 'none', flexWrap: 'wrap', maxWidth: '100%' }}
        >
          {actions}
        </Stack>
      )}
    </Box>
  );
}
